// hooks/useContractTabs.ts
import { useState, useMemo, useCallback } from 'react';
import { useDynamicTranslation } from 'hooks/useDynamicTranslation';
import { NsKey } from 'enums/NsKey';
import { ContractDetailsGlobal } from 'api/contract';
import { BranchCode } from 'api/risk';
import { useRoleCan } from 'core/hooks/useRoleCan';
import { Capability } from 'core/roles';

// Enum pour les onglets
export enum TabsEnum {
  GENERAL_INFORMATIONS,
  CONTRACT,
  DOCUMENTS,
  BILLING,
  CLAIMS,
  NOTES,
  HISTORY,
  NEW_NOTE,
  ADVANCED_DOCUMENTS,
  FUNCTIONALITY,
  TERMINATION,
  CONSTRUCTION,
  SEND_MAIL,
  COMPLAIN,
  SUSPEND,
  RESILIATION,
  ATTESTATION,
}

// Types d'actions possibles
export type TabActionType = 'navigate' | 'action' | 'modal' | 'external';

export interface TabAction {
  type: TabActionType;
  handler?: () => void | Promise<void>;
  modalConfig?: {
    title: string;
    component: React.ComponentType<any>;
    props?: any;
  };
  externalUrl?: string;
}

export interface TabConfig {
  value: TabsEnum;
  label: string;
  icon: string;
  description?: string;
  badge?: string | number;
  disabled?: boolean;
  category?: 'main' | 'documents' | 'actions' | 'advanced';
  // Nouveau : configuration de l'action
  action?: TabAction;
  // Indique si c'est un onglet de navigation classique ou une action
  isAction?: boolean;
}

interface UseSubContractTabsProps {
  contractDetails?: ContractDetailsGlobal;
  initialTab?: TabsEnum;
  documentsCount?: number;
  // Callbacks pour les actions
  onNewNote?: () => void;
  onSendMail?: () => void;
  onOpenModal?: (modalConfig: any) => void;
  onShowNotification?: (message: string, type: 'success' | 'error' | 'info') => void;
}

export const useSubContractTabs = ({
  contractDetails,
  initialTab = TabsEnum.CONTRACT,
  documentsCount = 0,
  onNewNote,
  onSendMail,
  onOpenModal,
  onShowNotification
}: UseSubContractTabsProps) => {
  const { t } = useDynamicTranslation([NsKey.Contract, NsKey.Common]);
  const [activeTab, setActiveTab] = useState<TabsEnum>(initialTab);
  // Page détail contrat : lecture seule intégrale hors Courtier (matrice centrale) —
  // toutes les actions + les notes (consultation incluse) sont retirées.
  const canDetailActions = useRoleCan(Capability.ContractDetailActions);
  const canNotes = useRoleCan(Capability.NotesAccess);
  // Même droit que les pastilles des listes : pas de badge « Historique » hors courtier.
  const canNotifBadges = useRoleCan(Capability.ListNotificationBadges);

  // Actions personnalisées
  const handleNewNoteAction = useCallback(async () => {
    // Naviguer vers l'onglet Notes et appeler le callback
    setActiveTab(TabsEnum.NOTES);
    onNewNote?.();
  }, [onNewNote]);

  const handleSendMailAction = useCallback(async () => {
    onSendMail?.();
  }, [onSendMail]);

  // Configuration des onglets avec logique métier et actions
  const tabsConfig = useMemo((): TabConfig[] => {
    const allTabs: TabConfig[] = [
    // {
    //   value: TabsEnum.GENERAL_INFORMATIONS,
    //   label: t('TITLE.GENERAL_INFORMATIONS'),
    //   icon: 'umbrella',
    //   description: 'Informations générales du contrat',
    //   category: 'main',
    //   isAction: false
    // },
    {
      value: TabsEnum.CONTRACT,
      label: t('CONTRACT.TABS.CONTRACT'),
      icon: 'piggyBank',
      description: 'Informations générales du contrat',
      category: 'main',
      isAction: false
    },
    {
      value: TabsEnum.DOCUMENTS,
      label: t('CONTRACT.TABS.DOCUMENTS'),
      icon: 'signature',
      description: 'Documents contractuels',
      category: 'main',
      isAction: false
    },
    {
      value: TabsEnum.BILLING,
      label: t('CONTRACT.TABS.BILLING'),
      icon: 'piggyBank',
      description: 'Facturation et paiements',
      category: 'main',
      isAction: false
    },
    {
      value: TabsEnum.CLAIMS,
      label: t('CONTRACT.TABS.CLAIMS'),
      icon: 'fire',
      description: 'Gestion des sinistres',
      category: 'main',
      isAction: false
    },
    {
      value: TabsEnum.NOTES,
      label: t('CONTRACT.TABS.NOTES'),
      icon: 'noteSticky',
      description: 'Notes et commentaires',
      category: 'main',
      isAction: false
    },
    {
      value: TabsEnum.COMPLAIN,
      label: t('CONTRACT.TABS.COMPLAIN'),
      icon: 'email',
      category: 'main',
      isAction: false
    },
    {
      value: TabsEnum.HISTORY,
      label: t('CONTRACT.TABS.HISTORY'),
      icon: 'history',
      description: 'Historique des modifications',
      badge: canNotifBadges && contractDetails?.unreadHistoryCount && contractDetails.unreadHistoryCount > 0 ? contractDetails.unreadHistoryCount : undefined,
      category: 'main',
      isAction: false
    },
    // ACTIONS - Ces onglets exécutent des actions au lieu de naviguer
    {
      value: TabsEnum.NEW_NOTE,
      label: t('CONTRACT.TABS.NEW_NOTE'),
      icon: 'pencil',
      description: 'Ajouter une nouvelle note',
      category: 'actions',
      isAction: true,
      action: {
        type: 'action',
        handler: handleNewNoteAction
      }
    },
    {
      value: TabsEnum.SEND_MAIL,
      label: t('COMMON.SEND_MAIL'),
      icon: 'send',
      description: 'Envoyer un email',
      category: 'actions',
      isAction: true,
      action: {
        type: 'action',
        handler: handleSendMailAction
      }
    },
    // {
    //   value: TabsEnum.SUSPEND,
    //   label: t('CONTRACT.TABS.SUSPEND'),
    //   icon: 'powerOff',
    //   description: 'Ajouter une nouvelle note',
    //   category: 'actions',
    //   isAction: true,
    //   action: {
    //     type: 'action',
    //     handler: handleNewNoteAction
    //   }
    // },
    // {
    //   value: TabsEnum.ATTESTATION,
    //   label: t('CONTRACT.TABS.ATTESTATION'),
    //   icon: 'download',
    //   category: 'actions',
    //   isAction: true,
    //   action: {
    //     type: 'action',
    //     handler: handleNewNoteAction
    //   }
    // },
    // {
    //   value: TabsEnum.RESILIATION,
    //   label: t('CONTRACT.TABS.RESILIATION'),
    //   icon: 'crossCircle',
    //   description: 'Résilier le contrat',
    //   category: 'actions',
    //   isAction: true,
    //   action: {
    //     type: 'action',
    //     handler: handleNewNoteAction
    //   }
    // },
    ];
    let tabs = allTabs;
    // Rôle sans droit d'action sur le détail (ex. Plateforme) : aucun onglet « actions ».
    if (!canDetailActions) {
      tabs = tabs.filter(tab => tab.category !== 'actions');
    }
    // Notes hors périmètre (consultation comprise) pour les rôles sans droit notes.
    if (!canNotes) {
      tabs = tabs.filter(tab => tab.value !== TabsEnum.NOTES && tab.value !== TabsEnum.NEW_NOTE);
    }
    // Contrats animaux : lecture seule pour la gestion. Le courtier ne déclare
    // pas de sinistre ni de réclamation — l'assuré gère via l'espace-membre.
    if (contractDetails?.branchCode === BranchCode.Pet) {
      return tabs.filter(tab => tab.value !== TabsEnum.CLAIMS && tab.value !== TabsEnum.COMPLAIN);
    }
    return tabs;
  }, [t, documentsCount, handleNewNoteAction, handleSendMailAction, contractDetails?.unreadHistoryCount, contractDetails?.branchCode, canDetailActions, canNotes, canNotifBadges]);

  // Onglets groupés par catégorie
  const groupedTabs = useMemo(() => {
    const groups = tabsConfig.reduce((acc, tab) => {
      const category = tab.category || 'main';
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push(tab);
      return acc;
    }, {} as Record<string, TabConfig[]>);

    return groups;
  }, [tabsConfig]);

  // Actions - Logique modifiée pour gérer les actions
  const handleTabChange = useCallback(async (value: TabsEnum) => {
    const tab = tabsConfig.find(t => t.value === value);

    if (!tab || tab.disabled) {
      return;
    }

    // Si c'est un onglet d'action, exécuter l'action au lieu de changer d'onglet
    if (tab.isAction && tab.action) {
      try {
        switch (tab.action.type) {
          case 'action':
            if (tab.action.handler) {
              await tab.action.handler();
            }
            break;

          case 'modal':
            if (tab.action.modalConfig && onOpenModal) {
              onOpenModal(tab.action.modalConfig);
            }
            break;

          case 'external':
            if (tab.action.externalUrl) {
              window.open(tab.action.externalUrl, '_blank');
            }
            break;

          default:
            console.warn(`Type d'action non géré: ${tab.action.type}`);
        }
      } catch (error) {
        console.error('Erreur lors de l\'exécution de l\'action:', error);
        onShowNotification?.('Erreur lors de l\'exécution de l\'action', 'error');
      }
    } else {
      // Comportement classique : changer d'onglet pour les onglets de navigation
      setActiveTab(value);
    }
  }, [tabsConfig, onOpenModal, onShowNotification]);

  const getActiveTab = useCallback(() => {
    return tabsConfig.find(tab => tab.value === activeTab) || tabsConfig[0];
  }, [activeTab, tabsConfig]);

  const getTabsByCategory = useCallback((category: string) => {
    return groupedTabs[category] || [];
  }, [groupedTabs]);

  const isTabActive = useCallback((tabValue: TabsEnum) => {
    return activeTab === tabValue;
  }, [activeTab]);

  const isTabDisabled = useCallback((tabValue: TabsEnum) => {
    const tab = tabsConfig.find(t => t.value === tabValue);
    return tab?.disabled || false;
  }, [tabsConfig]);

  const isTabAction = useCallback((tabValue: TabsEnum) => {
    const tab = tabsConfig.find(t => t.value === tabValue);
    return tab?.isAction || false;
  }, [tabsConfig]);

  // Navigation helpers (seulement pour les onglets de navigation)
  const getNavigationTabs = useCallback(() => {
    return tabsConfig.filter(tab => !tab.isAction && !tab.disabled);
  }, [tabsConfig]);

  const goToNextTab = useCallback(() => {
    const navigationTabs = getNavigationTabs();
    const currentIndex = navigationTabs.findIndex(tab => tab.value === activeTab);

    if (currentIndex < navigationTabs.length - 1) {
      setActiveTab(navigationTabs[currentIndex + 1].value);
    }
  }, [activeTab, getNavigationTabs]);

  const goToPrevTab = useCallback(() => {
    const navigationTabs = getNavigationTabs();
    const currentIndex = navigationTabs.findIndex(tab => tab.value === activeTab);

    if (currentIndex > 0) {
      setActiveTab(navigationTabs[currentIndex - 1].value);
    }
  }, [activeTab, getNavigationTabs]);

  return {
    // État
    activeTab,
    tabsConfig,
    groupedTabs,

    // Actions
    handleTabChange,
    setActiveTab,

    // Getters
    getActiveTab,
    getTabsByCategory,
    isTabActive,
    isTabDisabled,
    isTabAction,

    // Navigation (seulement pour les onglets de navigation)
    goToNextTab,
    goToPrevTab,
    getNavigationTabs,

    // Helpers
    enabledTabsCount: tabsConfig.filter(tab => !tab.disabled).length,
    totalTabsCount: tabsConfig.length,
    actionTabsCount: tabsConfig.filter(tab => tab.isAction).length,
    navigationTabsCount: tabsConfig.filter(tab => !tab.isAction).length,
  };
};
